import { hashCanonicalJson, sha256Text } from '../quote-engine/canonical-json';
import { plainTextToNotesHtml } from '../utils/notes-markdown';
import { classifyAiServiceFailure, createConsecutiveFailureCounter } from './ai-service-failure';
import { buildCompressedVideoFrameCloudMedia } from './cloud-media-policy';
import { recoverLocalVideoSource } from './local-video-source-recovery';
import { getPhotoBlob } from './photo-blob-store';
import { requireCanonicalProjectUuid } from './project-media-write-boundary';
import { hashQuoteEngineEvalFile } from './quote-engine-eval-file-hash';
import { createSelectedFileScope, fileMatchesSelectedScope } from './selected-file-scope';
import { MAX_SERVER_MEDIA_ENTITY_ID_LENGTH } from './server-media-entity-id';
import { extractStage1VideoEvidence } from './stage1-video-evidence';
import { getStage1VideoProcessingMode, VIDEO_CAPS } from './stage1-video-policy';
import { findOwnedVideoBlobId, getVideoBlob } from './video-blob-store';
import { getPlayableCloudVideoSource } from './video-storage-policy';
import { recordedVideoTranscript, transcriptBelongsInNotes, videoTranscriptNotesText } from './video-transcript-notes';
import {
    assertEvidencePreparationCurrent, checkpointPreparedEvidence,
    type ProjectEvidencePreparationOptions, type ProjectEvidencePreparationResult,
} from './project-evidence-preparation-types';

type Row = Record<string, any>;
interface VideoSource { file: File; kind: string; expectedSha256?: string }
interface VideoPreparationDependencies {
    resolveSource: (project: Row, video: Row, options: ProjectEvidencePreparationOptions) => Promise<VideoSource | null>;
    extract: typeof extractStage1VideoEvidence;
    hashSource: (file: File, signal?: AbortSignal) => Promise<string>;
    photoAvailable: (photo: Row, projectId: string) => Promise<boolean>;
    findOwnedBlobId: (projectId: string, video: Row) => Promise<number | null>;
}

const DEFERRED_VIDEO_WARNING_PREFIX = 'Video evidence deferred:';
// Long enough to cover one Quote Run's repeated preparation calls, short enough
// that a later deliberate retry tries the clip again.
const DEFERRED_VIDEO_RETRY_AFTER_MS = 15 * 60 * 1000;
// One fewer than the queue's three provider waits, on purpose. Measured live
// twice (Waverton, Westmead, 2026-09-20): the failing transcription calls pause
// every pool key, so the FIRST call after the deferral (the Project Summary)
// meets `all_keys_quarantined` and costs a wait of its own. At 3 that was the
// run's last wait and Stages 2 to 5 had none left. At 2 the clip still gets one
// retry after a full cooldown, and the project keeps one wait in reserve.
const TRANSIENT_SERVICE_FAILURES_BEFORE_DEFER = 2;
// In memory only: consecutive transient failures per project within this tab.
// A count that has gone quiet is forgotten, so a wobble hours later in a tab
// left open is a first failure again, not 'the third in a row'.
const TRANSIENT_SERVICE_FAILURES_QUIET_AFTER_MS = 20 * 60 * 1000;
const transientServiceFailuresByProject = createConsecutiveFailureCounter(TRANSIENT_SERVICE_FAILURES_QUIET_AFTER_MS);
// Short memory that the service was just found down, shared across projects in
// this tab. Any success clears it, so a recovered service is used straight away.
const TRANSCRIPTION_SERVICE_DOWN_MEMORY_MS = 10 * 60 * 1000;
let transcriptionServiceDownUntilMs = 0;

/** Test seam: forget the failure counts and the service-down memory. */
export function _resetTransientServiceFailuresForTests(): void {
    transientServiceFailuresByProject.reset();
    transcriptionServiceDownUntilMs = 0;
}

function isAbort(error: unknown): boolean {
    return (error as { name?: unknown } | null)?.name === 'AbortError';
}

/**
 * Operator directive (2026-07-29, project-intake-readiness.ts): a per-file gap
 * must never strand the whole project. A video whose bytes ARE durably held
 * but which cannot be turned into a transcript and keyframes (an iPhone HEVC
 * clip this browser cannot decode, a clip with no extractable frame) is
 * supplementary evidence that failed to process. It is recorded on the video
 * and rides along for human review; the RFQ documents and photos still quote.
 * Genuinely lost bytes are a different case and still stop, per the same rule.
 */
function deferVideoEvidence(project: Row, video: Row, reason: string): Row {
    const warning = `${DEFERRED_VIDEO_WARNING_PREFIX} ${reason}`;
    const nextVideo = {
        ...video,
        evidenceDeferred: true,
        requiresAttendedProcessing: true,
        warnings: [
            ...(Array.isArray(video.warnings) ? video.warnings : [])
                .filter((entry: unknown) => !String(entry).startsWith(DEFERRED_VIDEO_WARNING_PREFIX)),
            warning,
        ],
    };
    return { ...project, videos: project.videos.map((entry: Row) => entry.id === video.id ? nextVideo : entry) };
}

function failure(code: string, video: Row, detail: string) {
    return Object.assign(new Error(`Video "${video.name || video.originalName || video.id}": ${detail}`), {
        code, retryable: true, fileId: video.id,
    });
}

function ownsCloudKey(key: unknown, projectId: string): key is string {
    return typeof key === 'string' && key.split('/')[1] === 'projects' && key.split('/')[2] === projectId;
}

/** Only exact project-owned bytes; no filename search, stale object URLs or permission prompt. */
export async function resolveProjectVideoSource(
    project: Row, video: Row, options: ProjectEvidencePreparationOptions,
): Promise<VideoSource | null> {
    const check = () => assertEvidencePreparationCurrent(options);
    check();
    let blob = video.videoBlobId ? await getVideoBlob(video.videoBlobId, project.id) : null;
    check();
    let kind = 'project-video-blob';
    if (!blob) {
        blob = await recoverLocalVideoSource(project, video);
        check();
        kind = 'exact-connected-source';
    }
    if (blob) {
        if (Number(video.size) > 0 && blob.size !== Number(video.size)) {
            throw failure('PROJECT_VIDEO_SOURCE_CHANGED', video, 'saved source size has changed. Reimport this file before preparing a quote.');
        }
        return { file: new File([blob], video.originalName || video.name, {
            type: blob.type || video.contentType || 'video/mp4', lastModified: video.lastModified || 0,
        }), kind };
    }
    const cloud = getPlayableCloudVideoSource(video);
    if (!cloud || !ownsCloudKey(cloud.s3Key, project.id)) return null;
    const { getFileUrl } = await import('./file-upload-service');
    check();
    const url = await getFileUrl(cloud.s3Key);
    check();
    if (!url) return null;
    const response = await fetch(url, { signal: options.signal });
    check();
    if (!response.ok) throw failure('PROJECT_VIDEO_SOURCE_UNAVAILABLE', video, `cloud source could not be read (${response.status}). Retry or reconnect the source folder.`);
    if (Number(response.headers.get('content-length')) > VIDEO_CAPS.INLINE_TRANSCRIBE) {
        await response.body?.cancel();
        throw failure('PROJECT_VIDEO_SOURCE_TOO_LARGE', video, 'cloud source exceeds the supported processing limit.');
    }
    // Bound the download even when the server omits Content-Length.
    if (!response.body) return null;
    const reader = response.body.getReader();
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let bytes = 0;
    try {
        while (true) {
            const chunk = await reader.read();
            check();
            if (chunk.done) break;
            bytes += chunk.value.byteLength;
            if (bytes > VIDEO_CAPS.INLINE_TRANSCRIBE) throw failure('PROJECT_VIDEO_SOURCE_TOO_LARGE', video, 'cloud source exceeds the supported processing limit.');
            chunks.push(new Uint8Array(chunk.value));
        }
    } finally {
        await reader.cancel();
        reader.releaseLock();
    }
    const expectedBytes = cloud.kind === 'compressed-proxy' ? Number(video.cloudPlayback?.derivativeBytes) : Number(video.size);
    if (!bytes || (expectedBytes > 0 && bytes !== expectedBytes)) {
        throw failure('PROJECT_VIDEO_SOURCE_CHANGED', video, 'stored cloud source size no longer matches its receipt. Reconnect the original video.');
    }
    return { file: new File(chunks, video.originalName || video.name, {
        type: response.headers.get('content-type') || 'video/mp4', lastModified: 0,
    }), kind: cloud.kind, expectedSha256: video.cloudPlayback?.checksumSha256 };
}

async function photoAvailable(photo: Row, projectId: string): Promise<boolean> {
    if (typeof photo.data === 'string' && /^data:image\/(jpeg|webp|png);base64,.+/.test(photo.data)) return true;
    if (photo.blobId && await getPhotoBlob(photo.blobId, projectId)) return true;
    return ownsCloudKey(photo.s3Key || photo.cloudObjectKey, projectId);
}

function descriptor(video: Row) {
    return hashCanonicalJson({
        id: String(video.id), name: String(video.originalName || video.name || ''),
        size: Number(video.size) || 0, lastModified: Number(video.lastModified) || 0,
        relativePath: String(video.relativePath || video.localSourcePath || ''),
        // A local source remains canonical when its background cloud proxy is
        // later attached. Changing that owning blob still invalidates evidence.
        locator: video.videoBlobId ? { videoBlobId: video.videoBlobId } : {
            cloudKey: video.cloudPlayback?.s3Key || video.s3Key || null,
            fileMetadataId: video.cloudPlayback?.fileMetadataId || video.fileMetadataId || null,
            checksumSha256: video.cloudPlayback?.checksumSha256 || null,
            localSourcePath: video.localSourcePath || null,
        },
    });
}

/**
 * Import Projects names a file `<job folder>::<relative path>`, and a long job
 * folder name pushed the frame id past the limit (Potts Point, 201), so every
 * save of the job stopped. An id that fits is kept exactly as before, because
 * saved receipts and re-run matching compare these ids; only an id that would
 * not fit carries a hash of the video id in place of the id itself.
 */
export function buildVideoEvidenceFrameId(videoId: string, sourceSha256: string, index: number): string {
    const full = `video-evidence:${videoId}:${sourceSha256}:${index}`;
    if (full.length <= MAX_SERVER_MEDIA_ENTITY_ID_LENGTH) return full;
    return `video-evidence:${sha256Text(videoId)}:${sourceSha256}:${index}`;
}

function transcriptFor(project: Row, video: Row): string {
    return recordedVideoTranscript(video, project.videoTranscripts);
}

function patchVideoEvidence(
    project: Row, video: Row, transcript: string, frames: Row[], receipt: Row, duration: number | null, segments: any[], writeNotes: boolean,
) {
    const nextVideo = {
        ...video, transcript, duration: duration ?? video.duration, evidencePreparation: receipt,
        processed: true, preprocessed: true, requiresAttendedProcessing: false, sourceMissing: false,
        evidenceDeferred: false,
        // Only obsolete deterministic pending-evidence warnings are cleared.
        warnings: (video.warnings || []).filter((warning: unknown) => !String(warning).includes('transcript/keyframe evidence still requires Stage 1 processing')
            && !String(warning).startsWith(DEFERRED_VIDEO_WARNING_PREFIX)),
    };
    const transcriptRow = {
        videoId: video.id, id: video.id, fileName: video.name, relativePath: video.relativePath,
        text: transcript, fullText: transcript, segments, framesExtracted: frames.length,
        duration: duration ?? video.duration, evidencePreparation: receipt,
    };
    const notes = String(project.formData?.notes || '');
    // Only a clip's first transcript is written into the notes, with the header
    // a Stage 1 drop writes; a part of a split video names its part and time
    // range from its `splitSource` (video-transcript-notes.ts).
    const block = writeNotes && transcript ? plainTextToNotesHtml(videoTranscriptNotesText(video, transcript)) : '';
    const photos = [...(project.photos || [])];
    for (const frame of frames) {
        const index = photos.findIndex((photo: Row) => photo.id === frame.id);
        if (index < 0) photos.push(frame);
        else photos[index] = frame;
    }
    return {
        ...project, videos: project.videos.map((entry: Row) => entry.id === video.id ? nextVideo : entry), photos,
        videoTranscripts: [...(project.videoTranscripts || []).filter((entry: Row) => entry.videoId !== video.id), transcriptRow],
        // Never replace human notes.
        formData: { ...(project.formData || {}), notes: notes + block },
    };
}

/** Evidence only. The caller owns guarded local/backend persistence and UI adoption. */
export async function prepareProjectVideoEvidence(
    options: ProjectEvidencePreparationOptions,
    overrides: Partial<VideoPreparationDependencies> = {},
): Promise<ProjectEvidencePreparationResult> {
    const deps: VideoPreparationDependencies = {
        resolveSource: resolveProjectVideoSource, extract: extractStage1VideoEvidence,
        hashSource: (file, signal) => hashQuoteEngineEvalFile(file, { signal }), photoAvailable,
        findOwnedBlobId: findOwnedVideoBlobId,
        ...overrides,
    };
    const check = () => assertEvidencePreparationCurrent(options);
    check();
    requireCanonicalProjectUuid(options.project.id, 'prepareProjectVideoEvidence');
    let project = options.project;
    let changed = false;
    const scope = createSelectedFileScope(options.selectedFileIds);
    const selectedIds = (project.videos || []).filter((video: Row) => fileMatchesSelectedScope(video, scope)).map((video: Row) => video.id);
    for (const id of selectedIds) {
        check();
        let video = project.videos.find((entry: Row) => entry.id === id);
        const mode = getStage1VideoProcessingMode(Number(video.size));
        // A folder import keeps a video over the import limit as a row with no
        // bytes and its reason (preprocessed-row-builder); there is nothing to read.
        if (!mode.accepted && video.importRejected === true) continue;
        if (!mode.accepted) throw failure('PROJECT_VIDEO_SOURCE_TOO_LARGE', video, mode.statusMessage);
        if (mode.localReferenceOnly) continue;
        // Heal a lost pointer BEFORE anything reads the row. The receipt's
        // descriptor hash includes videoBlobId, so a row healed later would
        // fail its own "source changed" check on the next run. A row that lost
        // its locator while the bytes stayed in video_blobs is not a missing
        // video: re-bind it to the single blob this project provably owns.
        if (!video.videoBlobId) {
            const ownedBlobId = await deps.findOwnedBlobId(project.id, video);
            check();
            if (ownedBlobId) {
                const healed = { ...video, videoBlobId: ownedBlobId, sourceMissing: false };
                // Saved at once, not left to a later checkpoint. When the clip's
                // receipt already matches, nothing below saves, and the final
                // evidence fingerprint ignores videoBlobId: the heal then lived
                // only in memory, and intake readiness (which reads the stored
                // row) went on reporting the bytes as lost on every retry.
                project = await checkpointPreparedEvidence(options, {
                    ...project, videos: project.videos.map((entry: Row) => entry.id === id ? healed : entry),
                });
                check();
                changed = true;
                video = project.videos.find((entry: Row) => entry.id === id) || healed;
            }
        }
        let transcript = transcriptFor(project, video);
        // The transcript this clip held before this run. Its words are already in the notes (video-transcript-notes.ts).
        const previousTranscript = transcript;
        const prior = video.evidencePreparation;
        if (prior?.kind === 'video' && prior.sourceDescriptorHash !== descriptor(video)) {
            throw failure('PROJECT_VIDEO_SOURCE_CHANGED', video, 'the original source was replaced after its evidence was prepared. Start a fresh import with the correct source before generating a quote; existing notes and frames have been retained.');
        }
        const frames = (project.photos || []).filter((photo: Row) => photo.videoId === video.id && photo.fromVideo
            && (!Array.isArray(prior?.frameIds) || prior.frameIds.includes(photo.id)));
        let existingFramesAvailable = frames.length > 0;
        for (const frame of frames) {
            if (!await deps.photoAvailable(frame, project.id)) existingFramesAvailable = false;
            check();
        }
        const priorValid = prior?.status === 'complete' && prior.projectId === project.id
            && prior.sourceId === video.id && prior.sourceDescriptorHash === descriptor(video)
            && prior.transcriptHash === sha256Text(transcript)
            && JSON.stringify(prior.frameIds) === JSON.stringify(frames.map((frame: Row) => frame.id));
        // Attended Stage 1 already paid for these outputs. Verify actual frame
        // backing, then repair the shared transcript lane without repeating AI.
        if (existingFramesAvailable && (priorValid || (!prior && video.processed && transcript))) {
            const receipt = priorValid ? prior : {
                schemaVersion: 1, kind: 'video', status: 'complete', projectId: project.id, sourceId: video.id,
                sourceKind: 'existing-attended-evidence', sourceSha256: null, sourceDescriptorHash: descriptor(video),
                transcriptHash: sha256Text(transcript), frameIds: frames.map((frame: Row) => frame.id),
            };
            const existingTranscript = (project.videoTranscripts || []).find((entry: Row) => entry.videoId === video.id);
            // The writer that recorded this transcript already put it in the notes, so none is written here.
            const next = patchVideoEvidence(project, video, transcript, frames, receipt, video.duration, existingTranscript?.segments || [], false);
            if (!priorValid || existingTranscript?.text !== transcript || existingTranscript?.fullText !== transcript
                || !existingTranscript?.evidencePreparation || next.formData.notes !== project.formData?.notes) {
                project = await checkpointPreparedEvidence(options, next);
                changed = true;
            }
            continue;
        }
        // Already tried and deferred for this exact source a moment ago. Stage 1
        // prepares evidence twice (the runner, then Project Summary); without
        // this the same failing clip is hashed, decoded and transcribed twice
        // per run. A re-imported or replaced source has a new descriptor.
        const deferredAtMs = Date.parse(String(video.evidenceDeferredAt || ''));
        if (video.evidenceDeferred === true
            && video.evidenceDeferredDescriptor === descriptor(video)
            && Number.isFinite(deferredAtMs)
            && Date.now() - deferredAtMs < DEFERRED_VIDEO_RETRY_AFTER_MS) {
            // Say so: a silent skip reads as 'the video was used'.
            options.onProgress?.(0, `Video set aside for review a few minutes ago, not retried yet: ${video.name}`);
            continue;
        }
        const defer = async (reason: string) => {
            const stamped = { ...video, evidenceDeferredAt: new Date().toISOString(), evidenceDeferredDescriptor: descriptor(video) };
            project = await checkpointPreparedEvidence(options, deferVideoEvidence(project, stamped, reason));
            changed = true;
        };
        options.onProgress?.(0, `Preparing video evidence: ${video.name}`);
        // Full AI ignores a video it cannot get the bytes of (missing, empty,
        // changed in storage) and carries on (Peter, 2026-10-22).
        let source: Awaited<ReturnType<typeof deps.resolveSource>>;
        try {
            source = await deps.resolveSource(project, video, options);
        } catch (error) {
            const code = String((error as { code?: string })?.code || '');
            if (!options.setAsideUnreadableFiles || !code.startsWith('PROJECT_VIDEO_SOURCE_')) throw error;
            await defer((error as Error).message);
            continue;
        }
        check();
        if ((!source || source.file.size === 0) && options.setAsideUnreadableFiles) {
            await defer('the video file is empty or not stored, so it was set aside.');
            continue;
        }
        if (!source || source.file.size === 0) throw failure('PROJECT_VIDEO_SOURCE_UNAVAILABLE', video, 'the video file is not stored in this browser and has no cloud copy, so it cannot be read. Reimport the original file, or take this video out of the selection, then retry.');
        if (!getStage1VideoProcessingMode(source.file.size).processInline) throw failure('PROJECT_VIDEO_SOURCE_TOO_LARGE', video, 'source exceeds the supported processing limit.');
        const sourceSha256 = await deps.hashSource(source.file, options.signal);
        check();
        if (prior && (prior.sourceSha256 !== sourceSha256 || prior.sourceKind !== source.kind)) {
            throw failure('PROJECT_VIDEO_SOURCE_CHANGED', video, 'the exact source used for the saved evidence is unavailable or changed. Reconnect that original source or start a fresh import; existing notes and frames have been retained.');
        }
        if (source.expectedSha256 && source.expectedSha256.replace(/^sha256:/, '') !== sourceSha256.replace(/^sha256:/, '')) {
            throw failure('PROJECT_VIDEO_SOURCE_CHANGED', video, 'stored cloud source checksum no longer matches its receipt. Reconnect the original video.');
        }
        let evidence: Awaited<ReturnType<typeof extractStage1VideoEvidence>>;
        try {
            evidence = await deps.extract(source.file, {
                apiKey: options.apiKey, model: options.model, audioModel: options.audioModel,
                projectId: project.id, signal: options.signal, onProgress: options.onProgress,
                // Repair missing frame bytes without replacing a successfully
                // saved transcript (or charging to transcribe the same clip again).
                // An attended clip has no receipt, and its transcript is the one
                // in the notes: a second transcription would differ from it.
                ...(transcript && (!prior || prior.transcriptHash === sha256Text(transcript)) ? {
                    dependencies: { transcribe: async () => ({ text: transcript, segments: (project.videoTranscripts || []).find((entry: Row) => entry.videoId === video.id)?.segments || [] }) },
                } : {}),
            });
        } catch (error) {
            // A Stop or a superseded run is not a processing failure.
            if (isAbort(error) || options.signal?.aborted) throw error;
            // A problem with the AI service says nothing about this clip, so
            // it is not recorded against the clip straight away: deferring on
            // the first rate limit would discard evidence that a retry a minute
            // later would have produced. Let it surface, and the run waits the
            // service's cooldown and tries again.
            //
            // But a per-file gap must never strand the whole project. Live,
            // 2026-09-20: the transcription model answered 429 on every attempt
            // for over an hour while photo vision kept working, so every
            // project with a site video could never be quoted. Once the same
            // clip has met a TRANSIENT service failure this many times in a
            // row, defer it and quote from the documents and photos. Failures
            // that need the operator (credit, authentication, configuration,
            // data policy) always propagate: no clip will fare better.
            //
            // 'From the service' includes what classifyAiError has no wording
            // for: a gateway timeout, a cut stream, a dropped connection, an
            // expired session. Those were recorded as 'this browser could not
            // process the clip' on the first occurrence, with no retry.
            const aiFailure = classifyAiServiceFailure(error);
            if (aiFailure.fromService) {
                if (!aiFailure.transient) throw error;
                // Counted per PROJECT, not per clip, and not cleared by a
                // deferral. A site pack usually has several videos; once the
                // service has failed this many times running for the project it
                // is evidently down for this lane, so each remaining clip is
                // deferred at once instead of spending the run's bounded waits
                // three more times per clip and failing the project anyway.
                const failures = transientServiceFailuresByProject.increment(String(project.id));
                // A run usually holds several projects with site videos. Once one
                // project has established that the service is down, the next
                // ones should not each spend five minutes of waits rediscovering
                // it (and re-pausing the shared key pool each time they try).
                const laneKnownDown = Date.now() < transcriptionServiceDownUntilMs;
                if (failures < TRANSIENT_SERVICE_FAILURES_BEFORE_DEFER && !laneKnownDown) throw error;
                transcriptionServiceDownUntilMs = Date.now() + TRANSCRIPTION_SERVICE_DOWN_MEMORY_MS;
                check();
                await defer(`the AI transcription service was unavailable on ${failures} attempts in a row (${aiFailure.title || 'AI service error'}). It stays attached for human review; retry later to add its transcript and frames.`);
                continue;
            }
            check();
            const detail = String((error as { message?: unknown } | null)?.message || error || 'unknown error').slice(0, 160);
            await defer(`this browser could not process the clip (${detail}). It stays attached for human review.`);
            continue;
        }
        check();
        // The service answered: only CONSECUTIVE failures count toward a deferral.
        transientServiceFailuresByProject.clear(String(project.id));
        transcriptionServiceDownUntilMs = 0;
        transcript = String(evidence.transcript?.text || '').trim();
        const readableFrames = evidence.frames.filter(frame => /^data:(image\/(?:jpeg|webp));base64,(.+)$/.test(frame.data));
        if (readableFrames.length === 0) {
            await defer('no usable frame could be extracted from the clip. It stays attached for human review.');
            continue;
        }
        const preparedFrames = readableFrames.map((frame, index) => {
            const encoded = frame.data.match(/^data:(image\/(?:jpeg|webp));base64,(.+)$/);
            if (!encoded) throw failure('PROJECT_VIDEO_EVIDENCE_INCOMPLETE', video, 'an extracted frame is unreadable. Retry the video.');
            const frameId = buildVideoEvidenceFrameId(String(video.id), sourceSha256, index);
            const derivativeBytes = Math.floor(encoded[2]!.replace(/=+$/, '').length * 3 / 4);
            return {
                id: frameId, name: frame.name, originalName: frame.name, data: frame.data,
                timestamp: frame.timestamp, label: frame.label, context: frame.context,
                fromVideo: true, videoId: video.id, sourceRelativePath: video.relativePath,
                evidencePreparationHash: sha256Text(frame.data),
                cloudMedia: buildCompressedVideoFrameCloudMedia({
                    derivativeBytes, contentType: encoded[1]!, processor: 'browser-video-frame-extractor',
                }),
            };
        });
        const receipt = {
            schemaVersion: 1, kind: 'video', status: 'complete', projectId: project.id, sourceId: video.id,
            sourceSha256, sourceKind: source.kind, sourceDescriptorHash: descriptor(video),
            transcriptHash: sha256Text(transcript), frameIds: preparedFrames.map(frame => frame.id),
            extractionReceiptHash: evidence.receipt.receiptHash,
        };
        project = await checkpointPreparedEvidence(options, patchVideoEvidence(project, video, transcript, preparedFrames, receipt,
            evidence.duration, evidence.transcript?.segments || [], transcriptBelongsInNotes(previousTranscript, transcript)));
        changed = true;
    }
    check();
    return { project, changed };
}
